import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2, RefreshCw, Send, Save, Plus, Edit, Eye } from 'lucide-react';
import { api } from '@/services/api';

interface EmailSettings {
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_pass: string;
  smtp_secure: boolean;
  sender_name: string;
  sender_email: string;
}

interface EmailTemplate {
  id: string;
  code: string;
  name: string;
  subject: string;
  body: string;
  variables: string[];
}

interface EmailLog {
  id: string;
  recipient: string;
  subject: string;
  template_code: string;
  status: string;
  error: string;
  created_at: string;
}

export default function EmailManagementPage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('logs');
  const [isLoading, setIsLoading] = useState(false);

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-3xl font-bold mb-6">Gerenciamento de Emails</h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-8">
          <TabsTrigger value="logs">Logs de Envio</TabsTrigger>
          <TabsTrigger value="send">Enviar Email</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="settings">Configurações</TabsTrigger>
        </TabsList>

        <TabsContent value="logs">
          <EmailLogsTab />
        </TabsContent>

        <TabsContent value="send">
          <SendEmailTab />
        </TabsContent>

        <TabsContent value="templates">
          <EmailTemplatesTab />
        </TabsContent>

        <TabsContent value="settings">
          <EmailSettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EmailSettingsTab() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { register, handleSubmit, reset, setValue, watch } = useForm<EmailSettings>();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/emails/settings');
      if (response.data) {
        const data = response.data;
        setValue('smtp_host', data.smtp_host);
        setValue('smtp_port', data.smtp_port);
        setValue('smtp_user', data.smtp_user);
        setValue('smtp_pass', data.smtp_pass);
        setValue('smtp_secure', data.smtp_secure);
        setValue('sender_name', data.sender_name);
        setValue('sender_email', data.sender_email);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as configurações.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: EmailSettings) => {
    setIsLoading(true);
    try {
      await api.post('/emails/settings', data);
      toast({
        title: 'Sucesso',
        description: 'Configurações salvas com sucesso.',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar as configurações.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configurações de SMTP</CardTitle>
        <CardDescription>Configure os dados do servidor de email para envio de notificações.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtp_host">Host SMTP</Label>
              <Input id="smtp_host" {...register('smtp_host')} placeholder="smtp.example.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_port">Porta SMTP</Label>
              <Input id="smtp_port" type="number" {...register('smtp_port', { valueAsNumber: true })} placeholder="587" />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtp_user">Usuário SMTP</Label>
              <Input id="smtp_user" {...register('smtp_user')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_pass">Senha SMTP</Label>
              <Input id="smtp_pass" type="password" {...register('smtp_pass')} />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="smtp_secure" 
              onCheckedChange={(checked) => setValue('smtp_secure', checked as boolean)}
              checked={watch('smtp_secure')}
            />
            <Label htmlFor="smtp_secure">Usar SSL/TLS (Secure)</Label>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="space-y-2">
              <Label htmlFor="sender_name">Nome do Remetente</Label>
              <Input id="sender_name" {...register('sender_name')} placeholder="FotUp Notificações" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sender_email">Email do Remetente</Label>
              <Input id="sender_email" {...register('sender_email')} placeholder="noreply@fotup.com" />
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Salvar Configurações
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function EmailTemplatesTab() {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/emails/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os templates.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
  };

  const handleSaveTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTemplate) return;

    setIsLoading(true);
    try {
      await api.put(`/emails/templates/${editingTemplate.id}`, {
        subject: editingTemplate.subject,
        body: editingTemplate.body,
      });
      toast({
        title: 'Sucesso',
        description: 'Template atualizado com sucesso.',
      });
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar o template.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Templates de Email</h2>
        <Button onClick={loadTemplates} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {templates.map((template) => (
          <Card key={template.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">{template.name}</CardTitle>
              <CardDescription className="font-mono text-xs">{template.code}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4 truncate">{template.subject}</p>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full" onClick={() => handleEdit(template)}>
                    <Edit className="h-4 w-4 mr-2" /> Editar Template
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Editar Template: {template.name}</DialogTitle>
                  </DialogHeader>
                  {editingTemplate && (
                    <form onSubmit={handleSaveTemplate} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Assunto</Label>
                        <Input 
                          value={editingTemplate.subject} 
                          onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Corpo (HTML)</Label>
                        <Textarea 
                          value={editingTemplate.body} 
                          onChange={(e) => setEditingTemplate({...editingTemplate, body: e.target.value})}
                          className="min-h-[300px] font-mono text-sm"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Variáveis Disponíveis</Label>
                        <div className="flex flex-wrap gap-2">
                          {Array.isArray(template.variables) && template.variables.map((v) => (
                            <span key={v} className="bg-muted px-2 py-1 rounded text-xs font-mono">
                              {`{{${v}}}`}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="submit" disabled={isLoading}>
                          {isLoading ? <Loader2 className="animate-spin h-4 w-4 mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                          Salvar Alterações
                        </Button>
                      </div>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function EmailLogsTab() {
  const { toast } = useToast();
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/emails/logs?limit=50');
      setLogs(response.data);
    } catch (error) {
      console.error('Error loading logs:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os logs.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Histórico de Envios</CardTitle>
          <CardDescription>Últimos 50 emails enviados pelo sistema.</CardDescription>
        </div>
        <Button onClick={loadLogs} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Destinatário</TableHead>
              <TableHead>Assunto</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Nenhum registro encontrado.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>{log.recipient}</TableCell>
                  <TableCell className="max-w-[200px] truncate" title={log.subject}>
                    {log.subject}
                  </TableCell>
                  <TableCell>
                    <span className="bg-muted px-2 py-1 rounded text-xs font-mono">
                      {log.template_code || 'AVULSO'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      log.status === 'SENT' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {log.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    {log.error && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-500">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Erro no Envio</DialogTitle>
                          </DialogHeader>
                          <div className="bg-muted p-4 rounded text-sm font-mono whitespace-pre-wrap">
                            {log.error}
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function SendEmailTab() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'template' | 'raw'>('raw');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);

  // Form states
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [variables, setVariables] = useState('{}');

  useEffect(() => {
    if (mode === 'template') {
      loadTemplates();
    }
  }, [mode]);

  const loadTemplates = async () => {
    try {
      const response = await api.get('/emails/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (mode === 'raw') {
        await api.post('/emails/send-raw', {
          to,
          subject,
          body,
        });
      } else {
        let parsedVariables = {};
        try {
          parsedVariables = JSON.parse(variables);
        } catch (e) {
          toast({
            title: 'Erro',
            description: 'JSON de variáveis inválido.',
            variant: 'destructive',
          });
          setIsLoading(false);
          return;
        }

        await api.post('/emails/send', {
          to,
          templateCode: selectedTemplate,
          variables: parsedVariables,
        });
      }

      toast({
        title: 'Sucesso',
        description: 'Email enviado com sucesso!',
      });

      // Reset form
      setTo('');
      setSubject('');
      setBody('');
      setVariables('{}');
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao enviar email. Verifique os logs.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Enviar Email</CardTitle>
        <CardDescription>Envie emails avulsos ou baseados em templates.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex space-x-4 mb-6">
          <Button 
            variant={mode === 'raw' ? 'default' : 'outline'} 
            onClick={() => setMode('raw')}
          >
            Texto Livre (Raw)
          </Button>
          <Button 
            variant={mode === 'template' ? 'default' : 'outline'} 
            onClick={() => setMode('template')}
          >
            Usar Template
          </Button>
        </div>

        <form onSubmit={handleSend} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="to">Para (Email)</Label>
            <Input 
              id="to" 
              type="email" 
              placeholder="exemplo@email.com" 
              value={to}
              onChange={(e) => setTo(e.target.value)}
              required 
            />
          </div>

          {mode === 'raw' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="subject">Assunto</Label>
                <Input 
                  id="subject" 
                  placeholder="Assunto do email" 
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="body">Corpo (HTML)</Label>
                <Textarea 
                  id="body" 
                  placeholder="<h1>Olá!</h1><p>Conteúdo do email...</p>" 
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  className="min-h-[200px] font-mono"
                  required 
                />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="template">Template</Label>
                <select 
                  id="template"
                  aria-label="Select template"
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  required
                >
                  <option value="">Selecione um template...</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.code}>{t.name} ({t.code})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="variables">Variáveis (JSON)</Label>
                <Textarea 
                  id="variables" 
                  placeholder='{"name": "João", "order_id": "123"}' 
                  value={variables}
                  onChange={(e) => setVariables(e.target.value)}
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  Variáveis disponíveis no template selecionado: {
                    selectedTemplate && templates.find(t => t.code === selectedTemplate)?.variables?.join(', ')
                  }
                </p>
              </div>
            </>
          )}

          <div className="flex justify-end pt-4">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Enviar Email
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
